import { useCallback, useEffect, useRef, useState } from 'react';
import { Select } from './Select';
import { FIELD_SELECTOR, TextInput, TextInputProps } from '@ardoq/forms';
import styled from 'styled-components';
import { stringToOption } from './utils';
import { useEffectOnce, useKeydown } from '@ardoq/hooks';
import { debounce, isEmpty } from 'lodash';
import { SelectProps } from '../types';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

const Wrapper = styled.div`
  position: relative;

  ${FIELD_SELECTOR}:first-child {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

// tabIndex=-1 (set where this is rendered) makes it focusable without
// entering the tab order; pinned to the corner so it has no layout footprint.
const FocusParkingElement = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
`;

type TextInputWithAutocompleteProps = {
  value?: string;
  options?: string[];
  loadOptions?: (inputValue: string) => Promise<string[]>;
  preloadOptionsOnFocus?: boolean;
  menuPortalTarget?: HTMLElement | null;
  styles?: SelectProps<string>['styles'];
  i18nLocale?: I18nLocale;
} & Omit<TextInputProps, 'value'>;

export const TextInputWithAutocomplete = ({
  options: initialOptions = [],
  value: initialValue = '',
  loadOptions,
  onFocus,
  onValueChange: onValueChangeProp,
  preloadOptionsOnFocus,
  debounceTime,
  menuPortalTarget,
  styles,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  ...inputProps
}: TextInputWithAutocompleteProps & { debounceTime?: number }) => {
  const [options, setOptions] = useState<string[]>(initialOptions);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>(initialValue);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const focusParkingRef = useRef<HTMLDivElement>(null);
  const hasPreloadedRef = useRef(false);

  // There are several issues regarding `filterGroups` when the query builder
  // state is updated repeated times quickly, and the root cause is still unknown.
  // Adding the possibility to debounce the update seems to fix the issue.
  // Also, disabing eslint because it works fine (debounce returns a fn)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnValueChange = useCallback(
    debounce((newValue: string) => {
      if (onValueChangeProp) onValueChangeProp(newValue);
    }, debounceTime),
    [onValueChangeProp, debounceTime]
  );

  const onValueChange = (newValue: string) => {
    if (!onValueChangeProp) return;

    setValue(newValue);

    if (debounceTime) {
      debouncedOnValueChange(newValue);
    } else {
      onValueChangeProp(newValue);
    }
  };

  useEffectOnce(() => {
    const handleBlur = ({ target: clickTarget }: MouseEvent) => {
      if (wrapperRef.current?.contains(clickTarget as Node | null)) return;
      setMenuIsOpen(false);
      // This piece of code might seem unnecessary, but it plays a crucial role
      // when <TextInputWithAutocomplete> is used inside a focus-trapping
      // container (e.g. a <StackPage> or a <Modal>). Without it, that
      // container would reset focus back onto <TextInput>, causing the
      // dropdown menu to be recreated.
      if (wrapperRef.current?.contains(document.activeElement)) {
        focusParkingRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleBlur);
    return () => document.removeEventListener('mousedown', handleBlur);
  });

  useKeydown(
    useCallback(
      (event: KeyboardEvent) => {
        if (event.key === 'Escape' && menuIsOpen) {
          setMenuIsOpen(false);
        }
      },
      [menuIsOpen]
    )
  );

  const handleLoadOptions = useCallback(
    async (newValue: string) => {
      if (!loadOptions) return;
      setIsLoading(true);
      setOptions([]);
      try {
        setOptions(await loadOptions(newValue));
      } finally {
        setIsLoading(false);
      }
    },
    [loadOptions]
  );
  useEffect(() => {
    // hasPreloadedRef (rather than isEmpty(options)) guards this: options
    // resets to [] on every fetch, so checking it would keep re-triggering
    // this effect while the menu stays open on a legitimately empty result.
    // Resetting the ref on close lets the next open preload fresh results.
    if (!menuIsOpen) {
      hasPreloadedRef.current = false;
      return;
    }
    if (preloadOptionsOnFocus && value === '' && !hasPreloadedRef.current) {
      hasPreloadedRef.current = true;
      handleLoadOptions('').catch(() => {
        hasPreloadedRef.current = false;
      });
    }
  }, [menuIsOpen, value, preloadOptionsOnFocus, handleLoadOptions]);
  useEffect(() => {
    onValueChange?.(value);
    if (loadOptions) handleLoadOptions(value).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  //
  const shouldShowOptionsBox =
    isEmpty(value) && isEmpty(options) ? false : menuIsOpen;

  return (
    <Wrapper ref={wrapperRef}>
      <Select
        menuIsOpen={shouldShowOptionsBox}
        blurInputOnSelect
        i18nLocale={i18nLocale}
        options={[
          {
            label: t(i18nLocale, 'select:suggestions', 'Suggestions'),
            options: options.map(stringToOption),
          },
        ]}
        value={stringToOption(value)}
        isLoading={isLoading}
        onValueChange={selectedValue => {
          setValue(selectedValue as string);
          setMenuIsOpen(false);
        }}
        isDisabled={inputProps.isDisabled}
        menuPortalTarget={menuPortalTarget}
        onBackdropClick={() => setMenuIsOpen(false)}
        styles={styles}
      />
      <TextInput
        {...inputProps}
        value={value}
        onValueChange={setValue}
        onFocus={event => {
          setMenuIsOpen(true);
          onFocus?.(event);
        }}
      />
      <FocusParkingElement ref={focusParkingRef} tabIndex={-1} />
    </Wrapper>
  );
};
